"use client";

import { useState, useEffect } from "react";

// Ping de connectivité réelle (M7). `navigator.onLine` renvoie `true` dès qu'une
// interface réseau existe, même sans accès Internet/serveur → l'UI affichait
// "En ligne" alors que la sync échouait. On confirme avec un GET léger périodique.
const PING_URL = "/api/health";
const PING_INTERVAL_MS = 60_000; // conservateur : évite de marteler le serveur
const PING_TIMEOUT_MS = 5_000;

// GET (pas HEAD) : Hono ne route pas automatiquement HEAD sur une route GET,
// un HEAD renverrait 404 → faux négatif permanent.
async function pingServer(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
    const res = await fetch(PING_URL, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
    });
    clearTimeout(timer);
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Hook de détection du statut réseau (online/offline).
 * Combine `navigator.onLine` + événements window (réaction immédiate) ET un ping
 * périodique du serveur (connectivité réelle, pas juste "une interface existe").
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(() => {
    if (typeof navigator !== "undefined") return navigator.onLine;
    return true; // SSR fallback
  });

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      // navigator.onLine === false → certainement hors ligne : inutile de pinger.
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        if (!cancelled) setIsOnline(false);
        return;
      }
      const reachable = await pingServer();
      if (!cancelled) setIsOnline(reachable);
    }

    const handleOnline = () => verify();
    const handleOffline = () => {
      if (!cancelled) setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    verify(); // vérification initiale
    const intervalId = setInterval(verify, PING_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return { isOnline };
}
